import { useAuth, useOrganizationList } from '@clerk/react-router';
import { useLingui } from '@lingui/react/macro';
import { Dropdown, useToast } from 'app-ui/components';
import { Avatar } from 'app-ui/primitives/data-display';

import { useCloseSidePanel } from '@/ui/layout/side-panel/hooks/useCloseSidePanel';

// The workspaces (Clerk organizations) the signed-in person belongs to.
// Picking one makes it active in Clerk; ClerkAuthGate then clears the cached
// data and loads the new workspace.
export const ClerkWorkspaceOptionItems = () => {
  const { t } = useLingui();
  const { enqueueToast } = useToast();
  const { orgId } = useAuth();
  const { closeSidePanel } = useCloseSidePanel();
  const { isLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true, keepPreviousData: true },
  });

  if (!isLoaded || userMemberships.isLoading) {
    return <Dropdown.Loading>{t`Loading workspaces`}</Dropdown.Loading>;
  }

  const handleWorkspaceSelect = async (organizationId: string) => {
    if (organizationId === orgId) {
      return;
    }

    // An open intervention belongs to the workspace being left.
    closeSidePanel();

    try {
      await setActive({ organization: organizationId });
    } catch {
      enqueueToast({
        variant: 'error',
        children: t`Couldn't switch workspace. Try again.`,
      });
    }
  };

  const handleShowMoreClick = () => {
    userMemberships.fetchNext?.();
  };

  return (
    <>
      {userMemberships.data?.map((membership) => (
        <Dropdown.OptionItem
          key={membership.id}
          selected={membership.organization.id === orgId}
          startIcon={
            <Avatar
              name={membership.organization.name}
              size="md"
              shape="rounded-square"
            />
          }
          onSelect={() => {
            void handleWorkspaceSelect(membership.organization.id);
          }}
        >
          {membership.organization.name}
        </Dropdown.OptionItem>
      ))}
      {userMemberships.hasNextPage && (
        <Dropdown.ActionItem closeOnClick={false} onClick={handleShowMoreClick}>
          {t`Show more workspaces`}
        </Dropdown.ActionItem>
      )}
    </>
  );
};
